import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { websiteApi } from "../../../api/websiteApi";
import { useNotifications } from "../../../context/NotificationContext";
import Modal from "../../../components/common/modals/Modal";
import {
  AlertTriangle,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import {
  DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  parseWebsiteOnboardingQuestions,
} from "../../client/StoreRequest/websiteOnboardingQuestions";
import "./WebsiteRequests.css";

const STATUSES = [
  "pending",
  "approved",
  "rejected",
  "in_progress",
  "ready_for_review",
  "live",
  "suspended",
];

const parseObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-ZA", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const formatStatus = (value) =>
  String(value || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not provided";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "Not provided";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const SubmissionSection = ({ title, icon, entries }) => {
  const visibleEntries = entries.filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0),
  );

  return (
    <section className="website-submission-section">
      <h3>
        <i className={`fas ${icon}`} aria-hidden="true" />
        {title}
      </h3>
      {visibleEntries.length ? (
        <dl className="website-submission-details-grid">
          {visibleEntries.map(([label, value]) => (
            <div className="website-submission-detail-item" key={label}>
              <dt>{label}</dt>
              <dd>{formatValue(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="website-submission-empty">
          No details were saved for this section.
        </p>
      )}
    </section>
  );
};

const WebsiteRequestList = () => {
  const { socket } = useNotifications();
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submission, setSubmission] = useState({
    open: false,
    loading: false,
    error: "",
    request: null,
  });
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [openActionsRequestId, setOpenActionsRequestId] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [actionError, setActionError] = useState("");
  const [questionSections, setQuestionSections] = useState(
    DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  );
  const [questionsError, setQuestionsError] = useState("");

  const loadRequests = useCallback(async () => {
    setError("");
    try {
      const response = await websiteApi.getAdminRequests();
      setRequests(response.data || []);
    } catch (loadError) {
      setError(
        loadError.response?.data?.error || "Could not load website requests.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    let isCurrent = true;
    const loadQuestions = async () => {
      try {
        const response = await websiteApi.getOnboardingQuestions();
        const configured = parseWebsiteOnboardingQuestions(response.data);
        if (isCurrent) {
          setQuestionSections(configured);
          setQuestionsError("");
        }
      } catch (questionError) {
        console.error("Could not load website onboarding questions:", questionError);
        if (isCurrent) {
          setQuestionsError(
            "Configured question labels could not be loaded. Answers will be shown using their field names.",
          );
        }
      }
    };
    loadQuestions();
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("website-request:updated", loadRequests);
    return () => socket.off("website-request:updated", loadRequests);
  }, [loadRequests, socket]);

  useEffect(() => {
    if (!openActionsRequestId) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!event.target.closest?.("[data-website-request-actions]")) {
        setOpenActionsRequestId(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpenActionsRequestId(null);
        setActionMenuPosition(null);
      }
    };
    const closeOnScroll = () => {
      setOpenActionsRequestId(null);
      setActionMenuPosition(null);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnScroll);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, [openActionsRequestId]);

  useEffect(() => {
    if (!submission.open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setSubmission((current) => ({ ...current, open: false }));
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [submission.open]);

  const filteredRequests = requests.filter((request) => {
    const query = search.trim().toLowerCase();
    const matchesStatus =
      status === "all" ||
      String(request.Status || "pending").toLowerCase() === status;
    const matchesQuery =
      !query ||
      [
        request.BusinessName,
        request.ContactName,
        request.Email,
        request.InvoiceNumber,
        request.PackageName,
        request.AccountName,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    return matchesStatus && matchesQuery;
  });

  const openSubmission = async (requestId) => {
    setSubmission({ open: true, loading: true, error: "", request: null });
    try {
      const response = await websiteApi.getAdminRequest(requestId);
      setSubmission({
        open: true,
        loading: false,
        error: "",
        request: response.data,
      });
    } catch (loadError) {
      setSubmission((current) => ({
        ...current,
        loading: false,
        error:
          loadError.response?.data?.error ||
          "Could not load the client submission. Please try again.",
      }));
    }
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete) return;
    const requestId = requestToDelete.WebsiteRequestID;
    setDeletingRequestId(requestId);
    setActionError("");
    try {
      await websiteApi.deleteAdminRequest(requestId);
      setRequests((current) =>
        current.filter((request) => request.WebsiteRequestID !== requestId),
      );
      setSubmission((current) =>
        current.request?.WebsiteRequestID === requestId
          ? { ...current, open: false, request: null }
          : current,
      );
      setRequestToDelete(null);
    } catch (deleteError) {
      setActionError(
        deleteError.response?.data?.error ||
          "Could not permanently delete this website request.",
      );
    } finally {
      setDeletingRequestId(null);
    }
  };

  const toggleActionsMenu = (event, requestId) => {
    if (openActionsRequestId === requestId) {
      setOpenActionsRequestId(null);
      setActionMenuPosition(null);
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const menuHeight = 124;
    const menuWidth = 196;
    const top =
      bounds.bottom + menuHeight + 8 > window.innerHeight
        ? Math.max(8, bounds.top - menuHeight - 6)
        : bounds.bottom + 6;
    const left = Math.min(
      Math.max(8, bounds.right - menuWidth),
      window.innerWidth - menuWidth - 8,
    );
    setActionMenuPosition({ top, left });
    setOpenActionsRequestId(requestId);
  };

  const renderSubmissionModal = () => {
    if (!submission.open) return null;
    const request = submission.request;
    const domain = parseObject(request?.DomainData);
    const invoice = parseObject(request?.InvoiceData);
    const answers = parseObject(request?.Answers);
    const currency = invoice.currency || request?.LatestPaymentCurrency || "ZAR";
    const domainName =
      domain.domainType === "custom"
        ? `${domain.customDomain || ""}${domain.selectedExtension || ""}`
        : domain.domainType === "existing"
          ? domain.existingDomain
          : domain.subdomain
            ? `${domain.subdomain}.tooraflex.co.za`
            : "";
    const knownQuestionIds = new Set(
      questionSections.flatMap((section) =>
        section.questions.map((question) => question.id),
      ),
    );
    const unconfiguredAnswers = Object.entries(answers).filter(
      ([questionId, answer]) =>
        !knownQuestionIds.has(questionId) &&
        answer !== undefined &&
        answer !== null &&
        answer !== "" &&
        !(Array.isArray(answer) && !answer.length),
    );

    return (
      <div
        className="website-submission-modal-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            setSubmission((current) => ({ ...current, open: false }));
          }
        }}
      >
        <section
          className="website-submission-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="website-submission-modal-title"
        >
          <header className="website-submission-modal-header">
            <div>
              <span className="website-submission-modal-eyebrow">
                Client submission
              </span>
              <h2 id="website-submission-modal-title">
                {request?.BusinessName || "Website request"}
              </h2>
              {request && (
                <p>
                  {request.InvoiceNumber || request.WebsiteRequestID} ·{" "}
                  {request.PackageName || "Package not available"}
                </p>
              )}
            </div>
            <div className="website-submission-modal-header-actions">
              {request && (
                <Link
                  className="website-submission-manage-link"
                  to={`/admin/website-requests/${encodeURIComponent(request.WebsiteRequestID)}`}
                  onClick={() =>
                    setSubmission((current) => ({ ...current, open: false }))
                  }
                >
                  <ExternalLink size={15} aria-hidden="true" />
                  Manage request
                </Link>
              )}
              <button
                type="button"
                className="website-submission-modal-close"
                aria-label="Close submission details"
                onClick={() =>
                  setSubmission((current) => ({ ...current, open: false }))
                }
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </header>
          <div className="website-submission-modal-body">
            {submission.loading ? (
              <div className="website-submission-loading" role="status">
                <i className="fas fa-spinner fa-spin" />
                Loading submitted details…
              </div>
            ) : submission.error ? (
              <div className="website-requests-error" role="alert">
                {submission.error}
              </div>
            ) : request ? (
              <>
                <div className="website-submission-progress">
                  <span>Request status</span>
                  <strong>{formatStatus(request.Status)}</strong>
                  <span>Submitted {formatDate(request.CreatedAt)}</span>
                </div>
                <SubmissionSection
                  title="1. Website package"
                  icon="fa-box-open"
                  entries={[
                    ["Package", request.PackageName],
                    [
                      "Package price",
                      request.PackagePrice == null
                        ? "Custom quote"
                        : `${currency} ${Number(request.PackagePrice).toFixed(2)} ${request.PackagePriceLabel || ""}`,
                    ],
                  ]}
                />
                <SubmissionSection
                  title="2. Business and project details"
                  icon="fa-building"
                  entries={[
                    ["Business", request.BusinessName],
                    ["Contact", request.ContactName],
                    ["Email", request.Email || request.AccountEmail],
                    ["Phone", request.Phone],
                    ["Existing website", request.ExistingWebsite],
                    ["Project description", request.ProjectDescription],
                  ]}
                />
                <section className="website-submission-section">
                  <h3>
                    <i className="fas fa-clipboard-list" aria-hidden="true" />
                    Website project questions
                  </h3>
                  {questionsError && (
                    <p className="website-submission-question-warning" role="status">
                      {questionsError}
                    </p>
                  )}
                  {questionSections.map((section) => {
                    const answeredQuestions = section.questions.filter(
                      (question) =>
                        answers[question.id] !== undefined &&
                        answers[question.id] !== null &&
                        answers[question.id] !== "" &&
                        !(
                          Array.isArray(answers[question.id]) &&
                          !answers[question.id].length
                        ),
                    );
                    if (!answeredQuestions.length) return null;
                    return (
                      <div
                        className="website-submission-question-group"
                        key={section.title}
                      >
                        <h4>{section.title}</h4>
                        {section.description && <p>{section.description}</p>}
                        <dl className="website-submission-details-grid">
                          {answeredQuestions.map((question) => (
                            <div
                              className="website-submission-detail-item"
                              key={question.id}
                            >
                              <dt>{question.label}</dt>
                              <dd>{formatValue(answers[question.id])}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    );
                  })}
                  {unconfiguredAnswers.length > 0 && (
                    <div className="website-submission-question-group">
                      <h4>Other submitted answers</h4>
                      <dl className="website-submission-details-grid">
                        {unconfiguredAnswers.map(([questionId, answer]) => (
                          <div
                            className="website-submission-detail-item"
                            key={questionId}
                          >
                            <dt>
                              {questionId
                                .replace(/([a-z])([A-Z])/g, "$1 $2")
                                .replace(/[_-]/g, " ")
                                .replace(/^./, (letter) => letter.toUpperCase())}
                            </dt>
                            <dd>{formatValue(answer)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  {!Object.keys(answers).length && (
                    <p className="website-submission-empty">
                      No project-question answers were submitted.
                    </p>
                  )}
                </section>
                <SubmissionSection
                  title="3. Domain selection"
                  icon="fa-globe"
                  entries={[
                    ["Domain option", domain.domainType],
                    ["Selected domain", domainName],
                    ["Existing domain", domain.existingDomain],
                    ["Subdomain", domain.subdomain],
                    [
                      "Availability",
                      domain.availability?.available === true
                        ? "Available"
                        : domain.availability?.available === false
                          ? "Unavailable"
                          : undefined,
                    ],
                  ]}
                />
                <SubmissionSection
                  title="4. Agreement"
                  icon="fa-file-signature"
                  entries={[
                    ["Agreement", request.ContractName],
                    ["Accepted", Boolean(request.ContractAcceptedAt)],
                    [
                      "Accepted at",
                      request.ContractAcceptedAt
                        ? formatDate(request.ContractAcceptedAt)
                        : undefined,
                    ],
                    ["Agreement content", request.ContractContent],
                  ]}
                />
                <SubmissionSection
                  title="5. Invoice and payment"
                  icon="fa-file-invoice-dollar"
                  entries={[
                    ["Invoice number", request.InvoiceNumber],
                    [
                      "Package amount",
                      invoice.packageAmount == null
                        ? undefined
                        : `${currency} ${Number(invoice.packageAmount).toFixed(2)}`,
                    ],
                    [
                      "Domain amount",
                      invoice.domainAmount == null
                        ? undefined
                        : `${currency} ${Number(invoice.domainAmount).toFixed(2)}`,
                    ],
                    [
                      "Domain tax",
                      invoice.domainTax == null
                        ? undefined
                        : `${currency} ${Number(invoice.domainTax).toFixed(2)}`,
                    ],
                    [
                      "Total due",
                      invoice.totalDue == null
                        ? "Custom quote required"
                        : `${currency} ${Number(invoice.totalDue).toFixed(2)}`,
                    ],
                    ["Quote required", invoice.quoteRequired],
                    ["Payment status", request.PaymentStatus],
                    ["Payment method", request.LatestPaymentMethod],
                    ["Payment reference", request.LatestPaymentReference],
                  ]}
                />
              </>
            ) : null}
          </div>
        </section>
      </div>
    );
  };

  return (
    <main className="website-requests-page">
      <header className="website-requests-header">
        <div>
          <span className="website-requests-eyebrow">
            <i className="fas fa-globe" /> OPERATIONS
          </span>
          <h1>Website requests</h1>
          <p>Review client website submissions, quotes, invoices, and progress.</p>
        </div>
        <button
          className="website-requests-refresh"
          type="button"
          onClick={loadRequests}
          disabled={loading}
        >
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="website-requests-error" role="alert">
          {error}
        </div>
      )}

      <section className="website-requests-card">
        <div className="website-requests-toolbar">
          <label>
            <span>Search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Business, client, email, invoice..."
            />
          </label>
          <label>
            <span>Status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">All statuses</option>
              {STATUSES.map((item) => (
                <option key={item} value={item}>
                  {formatStatus(item)}
                </option>
              ))}
            </select>
          </label>
          <span className="website-requests-count">
            {filteredRequests.length} of {requests.length} requests
          </span>
        </div>

        {loading ? (
          <div className="website-requests-empty">
            <i className="fas fa-spinner fa-spin" /> Loading requests…
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="website-requests-empty">
            <i className="fas fa-inbox" />
            <strong>
              {requests.length ? "No matching requests" : "No website requests yet"}
            </strong>
            <span>New website submissions will appear here.</span>
          </div>
        ) : (
          <div className="website-requests-table-wrap">
            <table className="website-requests-table">
              <thead>
                <tr>
                  <th>Request / business</th>
                  <th>Client</th>
                  <th>Package</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => {
                  const requestId = request.WebsiteRequestID;
                  return (
                    <tr key={requestId}>
                      <td>
                        <strong>{request.BusinessName || "Website request"}</strong>
                        <small>{request.InvoiceNumber || requestId}</small>
                      </td>
                      <td>
                        <strong>
                          {request.ContactName || request.AccountName || "—"}
                        </strong>
                        <small>{request.Email || "—"}</small>
                      </td>
                      <td>{request.PackageName || "—"}</td>
                      <td>{formatDate(request.CreatedAt)}</td>
                      <td>
                        <span
                          className={`website-request-status status-${String(request.Status || "pending").toLowerCase()}`}
                        >
                          {formatStatus(request.Status)}
                        </span>
                      </td>
                      <td>
                        <div className="website-request-row-actions">
                          <button
                            className="request-actions-trigger"
                            type="button"
                            aria-label={`More actions for ${request.BusinessName || "website request"}`}
                            aria-haspopup="menu"
                            aria-expanded={openActionsRequestId === requestId}
                            title="More actions"
                            onClick={(event) =>
                              toggleActionsMenu(event, requestId)
                            }
                          >
                            <MoreHorizontal size={19} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openActionsRequestId &&
        actionMenuPosition &&
        createPortal(
          <div
            className="website-request-actions-popup"
            role="menu"
            data-website-request-actions
            style={{
              top: `${actionMenuPosition.top}px`,
              left: `${actionMenuPosition.left}px`,
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                const requestId = openActionsRequestId;
                setOpenActionsRequestId(null);
                setActionMenuPosition(null);
                openSubmission(requestId);
              }}
            >
              <Eye size={15} />
              View submission
            </button>
            <Link
              role="menuitem"
              to={`/admin/website-requests/${encodeURIComponent(openActionsRequestId)}`}
              onClick={() => {
                setOpenActionsRequestId(null);
                setActionMenuPosition(null);
              }}
            >
              <ExternalLink size={15} />
              Manage request
            </Link>
            <button
              type="button"
              role="menuitem"
              className="delete-menu-item"
              disabled={deletingRequestId === openActionsRequestId}
              onClick={() => {
                const request = requests.find(
                  (item) => item.WebsiteRequestID === openActionsRequestId,
                );
                setOpenActionsRequestId(null);
                setActionMenuPosition(null);
                setActionError("");
                setRequestToDelete(request || null);
              }}
            >
              <Trash2 size={15} />
              Delete request
            </button>
          </div>,
          document.body,
        )}

      {renderSubmissionModal()}
      {requestToDelete && (
        <Modal
          isOpen
          onClose={() => {
            if (!deletingRequestId) setRequestToDelete(null);
          }}
          title="Permanently delete website request?"
          closeOnOverlayClick={!deletingRequestId}
        >
          <div className="request-delete-confirmation">
            <div className="request-delete-warning">
              <AlertTriangle size={24} />
              <div>
                <strong>This action cannot be undone.</strong>
                <p>
                  Permanently delete{" "}
                  <strong>
                    {requestToDelete.BusinessName || "this website request"}
                  </strong>{" "}
                  ({requestToDelete.InvoiceNumber || "no invoice number"}) and
                  its payment records and website-request notifications? The
                  client account and separate support conversations will remain.
                </p>
              </div>
            </div>
            {actionError && (
              <p className="request-list-action-error" role="alert">
                {actionError}
              </p>
            )}
            <div className="request-delete-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setRequestToDelete(null)}
                disabled={Boolean(deletingRequestId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger-solid"
                onClick={confirmDeleteRequest}
                disabled={Boolean(deletingRequestId)}
              >
                <Trash2 size={15} />
                {deletingRequestId ? "Deleting…" : "Permanently delete"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </main>
  );
};

export default WebsiteRequestList;
